import React, { useEffect, useCallback } from "react";
import "./ImageLightbox.scss";
import { CloseOutlined } from '@ant-design/icons';
import { ChevronLeftIcon, ChevronRightIcon } from "@assets/icon";

interface ImageLightboxProps {
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onChangeIndex: (index: number) => void;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  images,
  currentIndex,
  onClose,
  onChangeIndex,
}) => {
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  const goPrev = useCallback(() => {
    if (hasPrev) onChangeIndex(currentIndex - 1);
  }, [hasPrev, currentIndex, onChangeIndex]);

  const goNext = useCallback(() => {
    if (hasNext) onChangeIndex(currentIndex + 1);
  }, [hasNext, currentIndex, onChangeIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, goPrev, goNext]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <button
      className="image-lightbox"
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('.image-lightbox__content')) return;
        onClose();
      }}
      style={{ border: 'none', background: 'none', padding: 0, width: '100%', height: '100%', textAlign: 'left' }}
    >
      <div className="image-lightbox__overlay" />

      {/* Close button */}
      <button
        className="image-lightbox__close"
        onClick={onClose}
        aria-label="Đóng"
        type="button"
      >
        <CloseOutlined />
      </button>

      {/* Counter */}
      {images.length > 1 && (
        <div className="image-lightbox__counter">
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Image */}
      <div className="image-lightbox__content">
        <img
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="image-lightbox__image"
          draggable={false}
        />
      </div>

      {/* Navigation arrows */}
      {hasPrev && (
        <button
          className="image-lightbox__nav image-lightbox__nav--prev"
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          aria-label="Ảnh trước"
          type="button"
        >
          <ChevronLeftIcon />
        </button>
      )}

      {hasNext && (
        <button
          className="image-lightbox__nav image-lightbox__nav--next"
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          aria-label="Ảnh tiếp"
          type="button"
        >
          <ChevronRightIcon />
        </button>
      )}
    </button>
  );
};

export default ImageLightbox;
